import Hls, {
  HlsConfig,
  Loader,
  LoaderCallbacks,
  LoaderConfiguration,
  LoaderContext
} from '@mediahub-bg/hls.js';

type CachedResponse = { data: string | ArrayBuffer; bytes: number; duration: number };

const CACHE_TTL = 60 * 1000;
const cache = new Map<string, CachedResponse & { time: number }>();
const prefetchedBitrates = new Map<string, number>();

const absolute = (url: string, base?: string) => new URL(url, base || window.location.href).href;

const store = (url: string, response: CachedResponse) => {
  const key = absolute(url);
  const entry = { ...response, time: Date.now() };
  cache.set(key, entry);
  setTimeout(() => cache.get(key) === entry && cache.delete(key), CACHE_TTL);
};

const take = (url: string) => {
  const key = absolute(url);
  const entry = cache.get(key);
  cache.delete(key);
  return entry && Date.now() - entry.time <= CACHE_TTL ? entry : undefined;
};

// XHR rather than fetch, which legacy TVs do not have
const download = (url: string, binary: boolean) =>
  new Promise<string | ArrayBuffer>((resolve, reject) => {
    const start = performance.now();
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = binary ? 'arraybuffer' : 'text';
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Prefetch of ${url} failed with ${xhr.status}`));
        return;
      }
      const data = xhr.response as string | ArrayBuffer;
      const bytes = typeof data === 'string' ? data.length : data.byteLength;
      store(url, { data, bytes, duration: performance.now() - start });
      resolve(data);
    };
    xhr.onerror = () => reject(new Error(`Prefetch of ${url} failed`));
    xhr.send();
  });

export const prefetchSource = async (source: string, bitrate: number, fragments = 2) => {
  if (prefetchedBitrates.has(source)) {
    return;
  }
  prefetchedBitrates.set(source, bitrate);
  try {
    const master = (await download(source, false)) as string;
    const levels: { bandwidth: number; url: string }[] = [];
    const levelRegex = /#EXT-X-STREAM-INF:[^\n]*BANDWIDTH=(\d+)[^\n]*\n([^\n#][^\n]*)/g;
    let match: RegExpExecArray | null;
    while ((match = levelRegex.exec(master)) !== null) {
      levels.push({ bandwidth: Number(match[1]), url: absolute(match[2].trim(), source) });
    }
    levels.sort((a, b) => a.bandwidth - b.bandwidth);
    if (!levels.length) {
      return;
    }
    const level = levels.filter((l) => l.bandwidth <= bitrate).pop() || levels[0];
    prefetchedBitrates.set(source, level.bandwidth);
    const playlist = (await download(level.url, false)) as string;
    const uris = playlist
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith('#'))
      .slice(0, fragments);
    await Promise.all(uris.map((uri) => download(absolute(uri, level.url), true)));
  } catch (error) {
    console.warn('Could not prefetch source', error);
  }
};

export const getPrefetchedBitrate = (source?: string) =>
  source ? prefetchedBitrates.get(source) : undefined;

export const clearPrefetch = (source?: string) => {
  source && prefetchedBitrates.delete(source);
};

const DefaultLoader = Hls.DefaultConfig.loader as unknown as new (
  config: HlsConfig
) => Loader<LoaderContext>;

// answers prefetched URLs from memory, keeping their real timings for ABR
export class PrefetchLoader extends DefaultLoader {
  load(
    context: LoaderContext,
    config: LoaderConfiguration,
    callbacks: LoaderCallbacks<LoaderContext>
  ) {
    const cached = take(context.url);
    if (!cached) {
      super.load(context, config, callbacks);
      return;
    }
    this.context = context;
    const { stats } = this;
    const now = performance.now();
    stats.loading.start = now - cached.duration;
    stats.loading.first = now;
    stats.loading.end = now;
    stats.loaded = stats.total = cached.bytes;
    setTimeout(() => {
      if (this.context === context) {
        callbacks.onSuccess({ url: context.url, data: cached.data }, stats, context, null);
      }
    });
  }
}
